import { DisclaimerDialogStatus, PatientSex } from '@models';

export interface LabResultCreateDto {
  patientName: string;
  patientBirthDate: string;
  patientGender: PatientSex;

  clinicalComplaintsSymptoms: string;
  clinicalDiagnosisIcdCode: string;
  clinicalCurrentMedicationsSupplements: string;
  clinicalDietaryRestrictionsPreferences: string;
  documentId: string;

  labPdf: File;
  labSpecimenCollectionDate: string;
  labOrderingProvider: string;
  labName: string;
  labTestName: string;
}

export interface LabResultDialogResult {
  labResult: LabResultCreateDto;
  status: 'submitted' | 'accept-required';
  analysisId?: string;
}

export interface AddLabResultDialogData {
  labResult?: LabResultCreateDto;
  disclaimerStatus: DisclaimerDialogStatus;
  errorMessage?: string;
  patientId: string;
}

/** Whether the practitioner has opened the current completed attempt. */
export enum LabAnalysisReviewStatus {
  NeedsReview = 'NeedsReview',
  Reviewed = 'Reviewed',
}

export enum LabAnalysisStatus {
  Queued = 'Queued',
  FileProcessing = 'FileProcessing',
  AnalysisProcessing = 'AnalysisProcessing',
  Complete = 'Complete',
  Failed = 'Failed',
}

export interface LabAnalysis {
  id: string;
  analysisGuid: string;
  analysisDate: string;
  labTest: string;
  labName: string;
  labDate: string;
  labLocation: string;
  labStatus: LabAnalysisStatus;
}

export interface LabAnalysisStatusResponse {
  reportId: string;
  status: LabAnalysisStatus;
}

export interface ClinicalReasoningDto {
  id: string;
  categorySystemImbalance: string | null;
  biomarkersOff: BiomarkerDto[];
  correlatedSymptoms: string | null;
  priority: string | null;
  confidence: number | null;
}
export interface BiomarkerDto {
  name: string | null;
  description: string | null;
  aboveRecommendedLevels: boolean;
}

export interface PersonalizedTimelineDto {
  id: string;
  step: string | null;
  start: string | null;
  dueBy: string | null;
  instructions: string | null;
  owner: string | null;
}

export interface LabAnalysisResult {
  systemImbalances: ClinicalReasoningDto[];
  privateTimeline: PersonalizedTimelineDto[];
  clinicalInterpretationCitations: string[];
  clinicalInterpretation: string | null;
  dietaryExplanation: string | null;
  lifestyleRecommendation: string | null;
  summary: string | null;
  patientHandout: string | null;
  medicationInsight: string | null;
  clinicalAlerts: string | null;
  originalFileUrl: string | null;
  htmlToPdfAnalysisUrl: string | null;
  htmlToPdfPatientHandoutUrl: string | null;
}

export interface LabAnalysisReport extends LabAnalysis {
  result: LabAnalysisResult;

  /**
   * The attempt this read returned. Sent back when marking the analysis reviewed, so a review
   * cannot land on a result a retry has since replaced.
   */
  attemptVersion?: number;

  /** Absent unless the analysis is complete; review only applies to a result. */
  reviewStatus?: LabAnalysisReviewStatus;

  /** When the analysis finished. Absent while processing, after a failure, and for legacy rows. */
  completedAtUtc?: string;
}
